import S from 's-js';
import SArray from 's-array';
import * as _ from 'underscore';
import TZDate from 'lib/TZDate';
import { IAjax } from 'lib/Ajax';
import { debounceSignal } from 'lib/debounceSignal';
import { tzmoment } from 'lib/tzmoment';
import { ConvertedMeasurement } from 'lib/ConvertedMeasurement';
import UOM from 'lib/UOM';
import { CaseInsensitiveContains } from 'lib/diacriticals';
import { Entrypoints } from 'core/app/entrypoints';
import { BerthClosuresDashboardOptions } from 'data/models/BerthClosuresDashboard';
import { BerthClosure } from 'data/models/BerthClosure';
import { IssueType } from 'data/models/IssueType';
import { NullableMeasurementLike } from 'data/models/Measurement';
import { UserSummary } from 'data/models/UserSummary';
import { BerthClosuresDashboardCache } from './cache';
import { toCSVDataUri, CSVField, ExcelDateFormat, ExcelTimeFormat } from 'common/services/csvExport';
import { GridColumn, RowSorter } from 'portcall/gridcomponents/model';
import { DateRangeSelector, DateRangeSelectorParams, DateRange } from 'portcall/daterangeselector/model';

// TODO: Localization!!
const DATE_TIME_FORMAT = 'MM/DD/YYYY [at] HH:mm';

const VERY_LONG_TIME_IN_THE_FUTURE = new TZDate(Date.now() + 2000 * 365.25 * 24 * 60 * 60 * 1000);

const
    StartDate : GridColumn<BerthClosureViewModel> = {
        Title: 'Start',
        DisplayValue: closure => closure.StartDateDisplay(),
        SortValue:    closure => closure.Model.StartDate(),
        ClassName: 'start-date'
    },
    EndDate : GridColumn<BerthClosureViewModel> = {
        Title: 'End',
        DisplayValue: closure => closure.EndDateDisplay(),
        SortValue:    closure => closure.Model.EndDate() || VERY_LONG_TIME_IN_THE_FUTURE,
        ClassName: 'end-date'
    },
    Title : GridColumn<BerthClosureViewModel> = {
        Title: 'Event',
        DisplayValue: closure => closure.TitleDisplay(),
        SortValue:    closure => closure.TitleDisplay(),
        ClassName: 'title'
    },
    Footmark : GridColumn<BerthClosureViewModel> = {
        Title: 'Ftmk S',
        DisplayValue: closure => closure.FootmarkDisplay(),
        SortValue:    closure => closure.Model.Footmark.convert(UOM.Foot).Num(),
        ClassName: 'footmark'
    },
    Endmark : GridColumn<BerthClosureViewModel> = {
        Title: 'Ftmk E',
        DisplayValue: closure => closure.EndmarkDisplay(),
        SortValue:    closure => closure.Endmark.convert(UOM.Foot).Num(),
        ClassName: 'endmark'
    },
    Berth : GridColumn<BerthClosureViewModel> = {
        Title: 'Berth',
        DisplayValue: closure => closure.BerthName(),
        SortValue:    closure => closure.BerthName(),
        ClassName: 'berth'
    },
    Port : GridColumn<BerthClosureViewModel> = {
        Title: 'Port/Region',
        DisplayValue: closure => closure.PortName(),
        SortValue:    closure => closure.PortName(),
        ClassName: 'port'
    },
    OverlapsCount : GridColumn<BerthClosureViewModel> = {
        Title: 'Overlaps',
        DisplayValue: closure => '' + closure.OverlapsCount(),
        SortValue:    closure => closure.OverlapsCount(),
        ClassName: 'overlaps-count'
    };

const DefaultSortColumn = StartDate;

export const Columns = [
    StartDate,
    EndDate,
    Title,
    Footmark,
    Endmark,
    Berth,
    Port,
    OverlapsCount
];

const CSVColumns : CSVField<BerthClosureViewModel>[] = [{
    name: `${StartDate.Title} (Date)`,
    formatter: closure => ExcelDateFormat(closure.Model.StartDate())
}, {
    name: `${StartDate.Title} (Time)`,
    formatter: closure => ExcelTimeFormat(closure.Model.StartDate())
}, {
    name: `${EndDate.Title} (Date)`,
    formatter: closure => closure.Model.EndDate() ? ExcelDateFormat(closure.Model.EndDate()!) : ''
}, {
    name: `${EndDate.Title} (Time)`,
    formatter: closure => closure.Model.EndDate() ? ExcelTimeFormat(closure.Model.EndDate()!) : ''
}, {
    name: Title.Title,
    formatter: Title.DisplayValue
}, {
    name: `${Footmark.Title} (feet)`,
    formatter: closure => convert(closure.Model.Footmark, UOM.Foot, 0)
}, {
    name: `${Footmark.Title} (meters)`,
    formatter: closure => convert(closure.Model.Footmark, UOM.Meter, 1)
}, {
    name: `${Endmark.Title} (feet)`,
    formatter: closure => convert(closure.Endmark, UOM.Foot, 0)
}, {
    name: `${Endmark.Title} (meters)`,
    formatter: closure => convert(closure.Endmark, UOM.Meter, 1)
}, {
    name: Berth.Title,
    formatter: Berth.DisplayValue,
}, {
    name: Port.Title,
    formatter: Port.DisplayValue
}, {
    name: OverlapsCount.Title,
    formatter: OverlapsCount.DisplayValue
}];

const filterStorageKey = 'portcall.berthclosuresdashboard.filter';

export interface BerthClosuresDashboardParams {
    orgId? : number;
    range : DateRange;
    from? : TZDate;
    to? : TZDate;
    filter? : string;
}

export type BerthClosuresDashboard = ReturnType<typeof BerthClosuresDashboard>;
export function BerthClosuresDashboard(
    ajax : IAjax,
    entrypoints : Entrypoints,
    cache : BerthClosuresDashboardCache,
    user : () => UserSummary | null,
    dateRangeSelectorModel : (params : DateRangeSelectorParams) => DateRangeSelector,
    params : BerthClosuresDashboardParams
) {
    const
        orgId = params.orgId === undefined ?
            user() ?
                user()!.OrganizationId() :
                0 :
            params.orgId,
        dateRangeSelector = dateRangeSelectorModel(params),
        loading = S.value(true),
        key = () : BerthClosuresDashboardOptions => ({
            OrganizationId: orgId,
            FromDate: dateRangeSelector.FromDate().toJSON(),
            ToDate: dateRangeSelector.ToDate().toJSON()
        }),
        model = S(() => cache.BerthClosuresDashboards.update1(null, key())),
        rowSorter = RowSorter(DefaultSortColumn),
        filter = S.data(params.filter || localStorage[filterStorageKey] as string | undefined || ''),
        debouncedFilter = debounceSignal(filter),
        closures = SArray<BerthClosure>([]),
        sortedClosures = closures.map(BerthClosureViewModel)
            .filter(closure => CaseInsensitiveContains(closure.FilterData(), debouncedFilter()))
            .sort(rowSorter.RowOrder),
        ShowActionsMenu = S.value(false),
        CSVExportLink = S(() => toCSVDataUri(sortedClosures(), CSVColumns));

    // Store filter
    S(() => {
        localStorage[filterStorageKey] = filter();
    });

    // Fetch data
    S(() => {
        loading(true);
        const req = ajax.Url<BerthClosuresDashboardOptions>(entrypoints.BerthClosuresDashboard(key())).Get();
        req.then(json => cache.BerthClosuresDashboards.update1(req.Date, json))
            .finally(() => loading(false));
    });

    S(() => closures(model().Closures()));

    return {
        OrgId: orgId,
        Organization: () => model().Organization(),
        Closures: sortedClosures,
        DateRangeSelector: dateRangeSelector,
        Filter: filter,
        Loading: loading,
        RowSorter: rowSorter,
        ShowActionsMenu,
        CSVExportLink
    };
}

export type BerthClosureViewModel = ReturnType<typeof BerthClosureViewModel>;
function BerthClosureViewModel(closure : BerthClosure) {
    const
        StartDateDisplay = () => tzmoment(closure.StartDate()).format(DATE_TIME_FORMAT),
        EndDateDisplay = () => closure.EndDate() ? tzmoment(closure.EndDate()!).format(DATE_TIME_FORMAT) : '--',
        TitleDisplay = () => closure.Title(),
        FootmarkDisplay = () => closure.Footmark.toString({ places: 0 }),
        Endmark = closure.Footmark.add(closure.Length),
        EndmarkDisplay = () => Endmark.toString({ places: 0 }),
        BerthName = () => closure.Berth() && closure.Berth()!.Location() && closure.Berth()!.Location()!.Name() || '--',
        PortName = () => closure.Berth() && closure.Berth()!.Location() && closure.Berth()!.Location()!.Port() && closure.Berth()!.Location()!.Port()!.Name() || '--',
        // NOTE: Keep in sync w/ portcall/schedulegrid/model (see BerthClosureModel).
        overlaps = closure.Issues.filter(i =>
            i.Type() === IssueType.BerthClosureOverlapsOther
        ).concat(closure.ReservationIssues.filter(i =>
            i.Type() === IssueType.ReservationOverlapsBerthClosure
        )),
        OverlapsCount = () => overlaps().length,
        FilterData = () =>
            StartDateDisplay() + ' ' +
            EndDateDisplay() + ' ' +
            TitleDisplay() + ' ' +
            FootmarkDisplay() + ' ' +
            EndmarkDisplay() + ' ' +
            BerthName() + ' ' +
            PortName() + ' ' +
            OverlapsCount(),
        ShowActionsMenu = S.value(false);

    return {
        Model: closure,
        StartDateDisplay,
        EndDateDisplay,
        TitleDisplay,
        FootmarkDisplay,
        Endmark,
        EndmarkDisplay,
        BerthName,
        PortName,
        OverlapsCount,
        FilterData,
        ShowActionsMenu
    };
}

function convert(v : NullableMeasurementLike, uom : string, precision : number) {
    return v.Num() === null ? '' : new ConvertedMeasurement(v, uom, precision).toString({ units: false })
}



// WEBPACK FOOTER //
// ./apps/portcall/berthclosuresdashboard/model.ts